import React, { useId, useRef, useState } from "react";

import PatternSections, { ScreenIntro } from "../PatternSections";
import { relearnPattern, undoLearning } from "../useLearning";
import SessionContext from "./SessionContext";
import { sessionIdOf } from "./useSessionContext";
import { CATALOG_STATE, CATALOG_TEXT, FIX_KIND_LABELS, errorText, fullWhen } from "./catalogText";

const C = CATALOG_TEXT.card;
const A = CATALOG_TEXT.actions;
const LABELS = Object.freeze({ symptoms: C.symptoms, steps: C.steps, rootCause: C.rootCause, fix: C.fix });

/** The detail popup's body for one manual learned entry: every field, with the actions the backend allows it: Edit (a
 * Learning editor pattern; opens the editor, owned by the page), Undo with an in-place confirm, and Learn again on an undone
 * entry outside the graph. `onChanged(notice)` runs after an undo or relearn succeeded. Plain text only. Laid out like the
 * Learning editor's preview (PatternSections), under how the conversation started (SessionContext). */
export default function CatalogDetail({ item, onEdit, onChanged }) {
  const id = useId();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false); // a double click lands before `busy` disables the button: one request per action
  const undone = item.state === CATALOG_STATE.UNDONE;
  const canUndo = !undone;
  const canRelearn = undone && !item.published;

  const run = async (call, notice) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      await call(item.id);
      setConfirming(false);
      onChanged(notice);
    } catch (e) {
      setError(errorText(e));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const tags = [
    item.published && { label: C.graph },
    undone && { label: C.undone, tone: "muted" },
  ];
  return (
    <section className="le-screen" aria-labelledby={`${id}-h`}>
      <ScreenIntro id={`${id}-h`} heading={[item.product, item.category_label || item.category].filter(Boolean).join(" · ")}
        meta={C.meta(item.saved_by || C.unknownUser, fullWhen(item.saved_at))} tags={tags} />
      <SessionContext sessionId={sessionIdOf(item)} />
      <PatternSections labels={LABELS} symptoms={item.symptoms} steps={item.resolution_steps} rootCause={item.root_cause}
        fixKindLabel={item.fix_kind ? (FIX_KIND_LABELS[item.fix_kind] || item.fix_kind) : ""} fixText={item.fix_text}
        empty={C.none} />
      {undone && item.undone_by && <p className="le-muted">{C.undoneMeta(item.undone_by, fullWhen(item.undone_at))}</p>}

      {error && <p className="le-error" role="alert">{error}</p>}
      {confirming ? (
        <div className="le-confirm" role="group" aria-label={A.undo}>
          <p className="le-text">{A.undoQuestion}</p>
          <div className="le-actions">
            <button type="button" className="le-btn le-btn--danger" disabled={busy}
              onClick={() => run(undoLearning, CATALOG_TEXT.notices.undone)}>{busy ? A.working : A.confirmUndo}</button>
            <button type="button" className="le-btn" disabled={busy}
              onClick={() => { setConfirming(false); setError(""); }}>{A.cancel}</button>
          </div>
        </div>
      ) : (
        <div className="le-actions">
          {item.editable && <button type="button" className="le-btn" onClick={() => onEdit(item)}>{A.edit}</button>}
          {canRelearn && (
            <button type="button" className="le-btn le-btn--primary" disabled={busy}
              onClick={() => run(relearnPattern, CATALOG_TEXT.notices.relearned)}>{busy ? A.working : A.relearn}</button>
          )}
          {canUndo && (
            <button type="button" className="le-btn" onClick={() => { setError(""); setConfirming(true); }}>{A.undo}</button>
          )}
        </div>
      )}
    </section>
  );
}
